import "chart.js/auto";
import type { ChartData, ChartOptions } from "chart.js";
import { useMemo } from "react";
import { Line } from 'react-chartjs-2';

type LegacyLinePoint = {
    name: string;
    value: number;
    title?: string | undefined;
};

type MultiLinePoint = {
    value: number;
    title?: string | undefined;
};

type MultiLineSeries = {
    key: string;
    label: string;
    fill?: boolean | undefined;
    values: MultiLinePoint[];
};

type MultiLineData = {
    labels: string[];
    series: MultiLineSeries[];
}

type Data = LegacyLinePoint[] | MultiLineData;

type Config = {
    isPercentage?: boolean;
    displayLegends?: boolean;
};

interface IProps {
    data: Data;
    config?: Config;
}

const colors: string[] = [
    "#5542F6",
    "#0093EE",
    "#00B67A",
    "#FF8A00",
    "#E53935",
    "#8E24AA",
    "#3949AB",
    "#00897B",
    "#6D4C41",
    "#546E7A",
] as const;

// Coerce a raw entry from a series' `values` array into the canonical { value, title? } shape.
// The documented contract is an array of objects, but Studio actions (and some legacy callers)
// often pass plain numbers — drawing nothing in that case is a surprising failure mode, so we
// accept either form here rather than fail silent.
function toMultiLinePoint(raw: unknown): MultiLinePoint {
    if (typeof raw === "number") return { value: raw };
    if (raw && typeof raw === "object" && "value" in raw) {
        const obj = raw as { value: number; title?: string };
        return { value: obj.value, title: obj.title };
    }
    return { value: 0 };
}

function LineChartWidget({
    config,
    data,
}: IProps): JSX.Element | null {
    const uniformData = useMemo<MultiLineData>(() => {
        if (Array.isArray(data)) {
            return {
                labels: data.map((x) => x.name),
                series: [{
                    key: "series1",
                    label: "",
                    values: data.map((x) => ({
                        value: x.value,
                        title: x.title,
                    })),
                }],
            };
        }
        // Multi-series form: normalise each series' values so plain-number arrays still render.
        return {
            labels: data.labels,
            series: data.series.map((s) => ({
                key: s.key,
                label: s.label,
                fill: s.fill,
                values: (s.values as unknown[] | undefined ?? []).map(toMultiLinePoint),
            })),
        };
    }, [data]);

    const chartData = useMemo<ChartData<"line", number[], string> | null>(() => {
        if (!uniformData) {
            return null;
        }

        return {
            labels: uniformData.labels,
            datasets: uniformData.series.map((series, index) => ({
                label: series.label,
                fill: series.fill ?? (uniformData.series.length === 1),
                data: series.values.map((x) => x.value),
                borderColor: colors[index],
                pointBackgroundColor: colors[index],
                tension: 0.4,
                backgroundColor: !series.fill && uniformData.series.length > 1                
                    ? undefined
                    : (context) => {
                        const ctx = context.chart.ctx;
                        const gradient = ctx.createLinearGradient(0, 0, 0, 125);
                        gradient.addColorStop(0, "rgba(85, 66, 246, 0.3)");
                        gradient.addColorStop(1, "rgba(85, 66, 246, 0)");
                        return gradient;
                    }
            })),
        };
    }, [uniformData]);

    const options = useMemo<ChartOptions<"line">>(() => ({
        layout: {
            padding: {
                top: 40,
                right: 25
            }
        },
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        hover: {
            mode: 'nearest',
            intersect: false
        },
        plugins: {
            legend: {
                display: config.displayLegends === undefined
                    ? uniformData.series.length > 1
                    : config.displayLegends,
                position: "bottom"
            },
            tooltip: {
                mode: "index",
                intersect: false,
                backgroundColor: '#3F4692',
                cornerRadius: 16,
                padding: 8,
                caretSize: 6,
                caretPadding: 6,
                yAlign: "bottom",
                displayColors: false,
                callbacks: {
                    label(tooltipItem) {
                        const value = tooltipItem.formattedValue + (config?.isPercentage ? "%" : "");

                        const series = uniformData.series[tooltipItem.datasetIndex];
                        if (!series) {
                            return `${tooltipItem.label}: ${value}`;
                        }

                        const item = series.values[tooltipItem.dataIndex];
                        if (!item?.title) {
                            return `${series.label || tooltipItem.label}: ${value}`;
                        }

                        return `${item.title}: ${value}`;
                    },
                    title: () => "",
                },
            },
        },
        scales: {
            y: {
                grid: {
                    tickLength: 0,
                    borderDash: [5, 5],
                    color: "#EAECF1",
                },
                beginAtZero: true,
                ticks: {
                    autoSkip: true,
                    maxTicksLimit: 5,
                    padding: 6,
                    font: {
                        size: 10,
                    }
                },
            },
            x: {
                grid: {
                    tickLength: 0,
                    color: "#EAECF1",
                },
                beginAtZero: true,
                ticks: {
                    padding: 6,
                    font: {
                        size: 10,
                    }
                },
            },
        }
    }), [uniformData, config]);

    return (
        <div className="LineChartWidget">
            <Line data={chartData}
                  options={options} />
        </div>
    );
}

export default LineChartWidget;
